import { GroupTypes } from "../enum/GroupTypes";
import { isNullish } from "./systemUtils";
import * as typeUtils from "./typeUtils";

//--------------------------------------------------------------------------------

const idSeparator = "\u2192";
const idArrow = "\u21D2";
const idMarkerRoot = "root";
const idMarkerFunction = "[function]";
const idMarkerParameter = "[parameter]";
const idMarkerChoice = "[choice]";
const idMarkerLogic = "[logic]";

const idBackSeparator = "::";

//--------------------------------------------------------------------------------

export const getIdSeparator = () => {
  return idSeparator;
};

export const getIdMarkerRoot = () => {
  return idMarkerRoot;
};

export const getIdMarkerFunction = () => {
  return idMarkerFunction;
};

export const getIdMarkerParameter = () => {
  return idMarkerParameter;
};

export const getIdMarkerChoice = () => {
  return idMarkerChoice;
};

export const getIdMarkerCondition = () => {
  return idMarkerLogic;
};

export const getIdBackSeparator = () => {
  return idBackSeparator;
};

export const isIdMarkerRoot = (segment) => {
  return segment === getIdMarkerRoot();
};

export const isIdMarkerFunction = (segment) => {
  return segment === getIdMarkerFunction();
};

export const isIdMarkerParameter = (segment) => {
  return segment === getIdMarkerParameter();
};

export const isIdMarkerChoice = (segment) => {
  return segment === getIdMarkerChoice();
};

export const isIdMarkerLogic = (segment) => {
  return segment === getIdMarkerCondition();
};

export const isIdMarker = (segment) => {
  return (
    isIdMarkerRoot(segment) ||
    isIdMarkerFunction(segment) ||
    isIdMarkerParameter(segment) ||
    isIdMarkerChoice(segment) ||
    isIdMarkerLogic(segment)
  );
};

//--------------------------------------------------------------------------------

export const getSeparatorGUI1 = () => {
  return idSeparator;
};

export const getSeparatorGUI2 = () => {
  return idArrow;
};

//--------------------------------------------------------------------------------

export const getIdPath = (id) => {
  return id.split(getIdSeparator());
};

//--------------------------------------------------------------------------------

export const isRoot = (id, config = {}) => {
  const { prevIsValidated } = config;

  if (!prevIsValidated) {
    validate(id);
  }

  return id === getIdMarkerRoot();
};

export const isFunction = (id, config = {}) => {
  const { prevIdPath, prevIsValidated } = config;

  if (!prevIsValidated) {
    validate(id);
  }

  return isElement(id, getIdMarkerFunction(), {
    prevIdPath,
    prevIsValidated: true,
  });
};

export const isParameter = (id, config = {}) => {
  const { prevIdPath, prevIsValidated } = config;

  if (!prevIsValidated) {
    validate(id);
  }

  return isElement(id, getIdMarkerParameter(), {
    prevIdPath,
    prevIsValidated: true,
  });
};

export const isChoice = (id, config = {}) => {
  const { prevIdPath, prevIsValidated } = config;

  if (!prevIsValidated) {
    validate(id);
  }

  return isElement(id, getIdMarkerChoice(), {
    prevIdPath,
    prevIsValidated: true,
  });
};

export const isCondition = (id, config = {}) => {
  const { prevIdPath, prevIsValidated } = config;

  if (!prevIsValidated) {
    validate(id);
  }

  return isElement(id, getIdMarkerCondition(), {
    prevIdPath,
    prevIsValidated: true,
  });
};

export const isParameterTop = (id, config = {}) => {
  const { prevIdPath, prevIsParameter, prevIsValidated } = config;

  if (!prevIsValidated) {
    validate(id);
  }

  const _isParameter = prevIsParameter ?? isParameter(id);

  if (!_isParameter) {
    return false;
  }

  const _idPath = prevIdPath ?? getIdPath(id);

  if (
    isParameterTopGlobal(id, {
      prevIdPath: _idPath,
      prevIsParameter: _isParameter,
      prevIsValidated: true,
    })
  ) {
    return true;
  }

  if (
    isParameterTopLocal(id, {
      prevIdPath: _idPath,
      prevIsParameter: _isParameter,
      prevIsValidated: true,
    })
  ) {
    return true;
  }

  return false;
};

export const isParameterTopGlobal = (id, config = {}) => {
  const { prevIdPath, prevIsParameter, prevIsValidated } = config;

  if (!prevIsValidated) {
    validate(id);
  }

  const _idPath = prevIdPath ?? getIdPath(id);

  if (_idPath.length !== 3) {
    return false;
  }

  return prevIsParameter ?? isParameter(id);
};

export const isParameterTopLocal = (id, config = {}) => {
  const { prevIdPath, prevIsParameter, prevIsValidated } = config;

  if (!prevIsValidated) {
    validate(id);
  }

  const _idPath = prevIdPath ?? getIdPath(id);

  if (_idPath.length !== 5) {
    return false;
  }

  return prevIsParameter ?? isParameter(id);
};

export const isParameterGlobal = (id, config = {}) => {
  const { prevIdPath, prevIsParameter, prevIsValidated } = config;

  if (!prevIsValidated) {
    validate(id);
  }

  const _isParameter = prevIsParameter ?? isParameter(id);

  if (!_isParameter) {
    return false;
  }

  const _idPath = prevIdPath ?? getIdPath(id);

  return (
    isParameterTopGlobal(id, {
      prevIdPath: _idPath,
      prevIsParameter: _isParameter,
      prevIsValidated: true,
    }) ||
    isParameterNestedGlobal(id, {
      prevIdPath: _idPath,
      prevIsParameter: _isParameter,
      prevIsValidated: true,
    })
  );
};

export const isParameterNestedGlobal = (id, config = {}) => {
  const { prevIdPath, prevIsParameter, prevIsValidated } = config;

  if (!prevIsValidated) {
    validate(id);
  }

  const _isParameter = prevIsParameter ?? isParameter(id);

  if (!_isParameter) {
    return false;
  }

  const _idPath = prevIdPath ?? getIdPath(id);

  return _idPath.at(1) !== idMarkerFunction && _idPath.length >= 3;
};

export const isParameterLocal = (id, config = {}) => {
  const { prevIdPath, prevIsParameter, prevIsValidated } = config;

  if (!prevIsValidated) {
    validate(id);
  }

  const _isParameter = prevIsParameter ?? isParameter(id);

  if (!_isParameter) {
    return false;
  }

  const _idPath = prevIdPath ?? getIdPath(id);

  return (
    isParameterTopLocal(id, {
      prevIdPath: _idPath,
      prevIsParameter: _isParameter,
      prevIsValidated: true,
    }) ||
    isParameterNestedLocal(id, {
      prevIdPath: _idPath,
      prevIsParameter: _isParameter,
      prevIsValidated: true,
    })
  );
};

export const isParameterNestedLocal = (id, config = {}) => {
  const { prevIdPath, prevIsParameter, prevIsValidated } = config;

  if (!prevIsValidated) {
    validate(id);
  }

  const _isParameter = prevIsParameter ?? isParameter(id);

  if (!_isParameter) {
    return false;
  }

  const _idPath = prevIdPath ?? getIdPath(id);

  return _idPath.at(1) === idMarkerFunction && _idPath.length >= 5;
};

export const isLocal = (id) => {
  return id.startsWith(`${getIdMarkerRoot()}${getIdSeparator()}${getIdMarkerFunction()}`);
};

export const isParent = (id, idRef) => {
  if (id && idRef) {
    const segmentsId = id.split(idSeparator).length;
    const segmentsIdRef = idRef.split(idSeparator).length;

    if (segmentsId === segmentsIdRef) {
      return false;
    }
  }

  return idRef.startsWith(id) && id !== idRef;
};

export const isDirectParent = (id, idRef) => {
  if (id && idRef) {
    const segmentsId = id.split(idSeparator).length;
    const segmentsIdRef = idRef.split(idSeparator).length;

    if (idRef.startsWith(id)) {
      if (segmentsIdRef === segmentsId + 2) {
        return true;
      }
    }
  }

  return false;
};

export const getIdParent = (id, config = {}) => {
  const { prevIdPath, prevIsValidated } = config;

  if (!prevIsValidated) {
    validate(id);
  }

  if (id === typeUtils.getTypeRoot()) {
    throw new Error("The root element does not have any parent!");
  }

  const _idPath = prevIdPath ?? getIdPath(id);
  const _candidate = _idPath.at(-2);

  if (isIdMarker(_candidate)) {
    return _idPath.slice(0, -2).join(idSeparator);
  }

  throw Error(`The parent of '${id}' could not be determined!`);
};

export const getName = (id, config = {}) => {
  const { prevIdPath } = config;

  if (!id) {
    return "";
  }

  const _idPath = prevIdPath ?? getIdPath(id);

  return _idPath.at(-1);
};

export const getNameFunction = (id, config = {}) => {
  const { prevIdPath } = config;

  if (!id) {
    return "";
  }

  const _idPath = prevIdPath ?? getIdPath(id);

  if (_idPath.at(1) === idMarkerFunction) {
    return _idPath.at(2);
  }

  return "";
};

export const getIdParameterTop = (id) => {
  let _candidate = id;

  while (!isParameterTopGlobal(_candidate) && !isParameterTop(_candidate)) {
    _candidate = getIdParent(_candidate);
  }

  return _candidate;
};

const isElement = (id, marker, config = {}) => {
  const { prevIdPath, prevIsValidated } = config;

  if (!prevIsValidated) {
    validate(id);
  }

  const _idPath = prevIdPath ?? getIdPath(id);

  return _idPath.at(-2) === marker;
};

const validate = (id) => {
  if (!id) {
    throw Error("The 'id' property is missing!");
  }
};

//--------------------------------------------------------------------------------

export const getNameSimplified = (id) => {
  return pathRemoveMarkers(pathRemoveRoot(id));
};

const pathRemoveRoot = (id) => {
  let parsed = id;
  parsed = parsed.replaceAll(`${getIdMarkerRoot()}${getIdSeparator()}`, "");

  return parsed;
};

const pathRemoveFunction = (id) => {
  const marker = `${idSeparator}${idMarkerFunction}${idSeparator}`;

  if (id.includes(marker)) {
    // eslint-disable-next-line no-unused-vars
    const [_s1, _s2, ...rest] = id.split(marker)[1].split(idSeparator);
    return rest.join(idSeparator);
  }

  return id;
};

const pathRemoveMarkers = (id) => {
  let parsed = id;
  parsed = parsed.replaceAll(idMarkerFunction, "");
  parsed = parsed.replaceAll(idMarkerParameter, "");
  parsed = parsed.replaceAll(idMarkerChoice, "");
  parsed = parsed.replaceAll(idMarkerLogic, "");

  const separator = getIdSeparator();
  parsed = parsed.replaceAll(`${separator}${separator}`, separator);

  if (parsed.startsWith(separator)) {
    parsed = parsed.substring(separator.length);
  }

  return parsed;
};

export const compareLevels = (id1, id2) => {
  const id1Length = id1.split(idSeparator)?.length;
  const id2Length = id2.split(idSeparator)?.length;

  return id2Length - id1Length;
};

export const getChoiceNestingLevel = (id, limit) => {
  if (!isChoice(id)) {
    return -1;
  }

  let _id = id;
  let _idParent = getIdParent(_id);
  let _level = 0;

  while (isChoice(_idParent)) {
    _level++;
    _id = _idParent;
    _idParent = getIdParent(_id);
  }

  if (_level < 0) {
    return undefined;
  }

  if (_level > limit) {
    return "limit";
  }

  return _level;
};

export const getParameterNestingLevel = (id, limit) => {
  if (!isParameter(id)) {
    return -1;
  }

  let _id = id;
  let _idParent = getIdParent(_id);
  let _level = 0;

  while (isParameter(_idParent)) {
    _level++;
    _id = _idParent;
    _idParent = getIdParent(_id);
  }

  if (_level < 0) {
    return undefined;
  }

  if (_level > limit) {
    return "limit";
  }

  return _level;
};

//--------------------------------------------------------------------------------

export const getIdElement = (name, type, idParent) => {
  if (type === typeUtils.getTypeFunction()) {
    idParent = typeUtils.getTypeRoot();
  }

  const separator = getIdSeparator();
  const prefix = `${idParent}${separator}`;
  const suffix = `${separator}${name}`;

  switch (type) {
    case typeUtils.getTypeRoot():
      return getIdMarkerRoot();
    case typeUtils.getTypeCondition():
      return `${prefix}${getIdMarkerCondition()}${suffix}`;
    case typeUtils.getTypeFunction():
      return `${prefix}${getIdMarkerFunction()}${suffix}`;
    case typeUtils.getTypeParameter():
      return `${prefix}${getIdMarkerParameter()}${suffix}`;
    case typeUtils.getTypeChoice():
      return `${prefix}${getIdMarkerChoice()}${suffix}`;
    default:
      return `${prefix}${name}`;
  }
};

export const parseId = (id, index) => {
  if (isNullish(index)) {
    return id;
  }

  return `${id}${getIdSeparator()}${index}`;
};

//--------------------------------------------------------------------------------

export const determineGroup = (idSrc, idDst) => {
  if (!idSrc || !idDst) {
    return GroupTypes.UNKNOWN;
  }

  let results = null;

  results = funSrc(idSrc, idDst);

  if (results) {
    return results;
  }

  results = funDst(idSrc, idDst);

  if (results) {
    return results;
  }

  return GroupTypes.INTERNAL_GLOBALS;
};

const funSrc = (idSrc, idDst) => {
  if (!isLocal(idSrc)) {
    return null;
  }

  if (isLocal(idDst)) {
    return GroupTypes.INTERNAL_FUNCTIONS;
  }

  return GroupTypes.TO_GLOBALS;
};

const funDst = (idSrc, idDst) => {
  if (!isLocal(idDst)) {
    return null;
  }

  if (isLocal(idSrc)) {
    return GroupTypes.INTERNAL_FUNCTIONS;
  }

  return GroupTypes.TO_FUNCTIONS;
};

//--------------------------------------------------------------------------------

export const cnvFunFrontToBackPath = (id) => {
  return pathRemoveFunction(id)
    .replaceAll(`${idSeparator}${idMarkerParameter}${idSeparator}`, idBackSeparator)
    .replaceAll(`${idSeparator}${idMarkerChoice}${idSeparator}`, idBackSeparator)
    .replaceAll(`${idSeparator}${idMarkerLogic}${idSeparator}`, idBackSeparator);
};

export const cnvBackPathToFunFront = (backendPath) => {

  if (!backendPath) {
    return "";
  }

  let result = idMarkerRoot;

  if (backendPath["function"]) {
    result += `${idSeparator}${idMarkerFunction}${idSeparator}${backendPath["function"]}`;

    if (backendPath["parameter"]) {
      const chain = backendPath['parameter'].split(idBackSeparator);
      result += chain.map(e => `${idSeparator}${idMarkerParameter}${idSeparator}${e}`).join('');
    }

    if (backendPath["choice"]) {
      const chain = backendPath['choice'].split(idBackSeparator);
      result += chain.map(e => `${idSeparator}${idMarkerChoice}${idSeparator}${e}`).join('');
    }

    if (backendPath["logic element"]) {
      const chain = backendPath['logic element'].split(idBackSeparator);
      result += chain.map(e => `${idSeparator}${idMarkerLogic}${idSeparator}${e}`).join('');
    }
  } else if (backendPath["global parameter"]) {
    result += `${idSeparator}${idMarkerParameter}${idSeparator}${backendPath["global parameter"]}`;

    if (backendPath["parameter"]) {
      const chain = backendPath['parameter'].split(idBackSeparator);
      result += chain.map(e => `${idSeparator}${idMarkerParameter}${idSeparator}${e}`).join('');
    }

    if (backendPath["choice"]) {
      const chain = backendPath['choice'].split(idBackSeparator);
      result += chain.map(e => `${idSeparator}${idMarkerChoice}${idSeparator}${e}`).join('');
    }

    if (backendPath["logic element"]) {
      const chain = backendPath['logic element'].split(idBackSeparator);
      result += chain.map(e => `${idSeparator}${idMarkerLogic}${idSeparator}${e}`).join('');
    }
  }

  return result;
};

export const getAiSuggestionObject = (id) => {
  const results = {
    type: "",
    function: "",
    parameter: [],
    choice: [],
    siblings: [],
    model: ""
  }

  const segments = id.split(getSeparatorGUI1());

  for (let i = 1 ; i < segments.length ; i++) {
    if (segments[i] === getIdMarkerFunction()) {
      results.function = segments[++i]
    }

    if (segments[i] === getIdMarkerParameter()) {
      results.parameter.push(segments[++i])
    }

    if (segments[i] === getIdMarkerChoice()) {
      results.choice.push(segments[++i])
    }
  }

  results.parameter = results.parameter.filter(e => e !== '').join(getIdBackSeparator());
  results.choice = results.choice.filter(e => e !== '').join(getIdBackSeparator());

  // results.parameter = results.parameter.join(getIdBackSeparator());
  // results.choice = results.choice.join(getIdBackSeparator())

  return results;
}